import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { CartItem, CartSummary } from '@/types';

interface CartState {
  items: CartItem[];
  isOpen: boolean;
  flyToCartTrigger: string | null;
  addItem: (item: CartItem) => void;
  removeItem: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  clearCart: () => void;
  toggleOpen: () => void;
  setOpen: (open: boolean) => void;
  triggerFlyToCart: (productId: string) => void;
  clearFlyToCartTrigger: () => void;
  getSummary: () => CartSummary;
  getItemCount: () => number;
  isInCart: (productId: string) => boolean;
}

const STORAGE_KEY = 'sggadgets_cart';

export const useCart = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      isOpen: false,
      flyToCartTrigger: null,

      addItem: (item) =>
        set((state) => {
          const existingIndex = state.items.findIndex((i) => i.productId === item.productId);

          if (existingIndex >= 0) {
            const updatedItems = [...state.items];
            const existing = updatedItems[existingIndex];
            const newQuantity = Math.min(existing.quantity + item.quantity, existing.maxQuantity);
            updatedItems[existingIndex] = { ...existing, quantity: newQuantity };
            return { items: updatedItems, flyToCartTrigger: item.productId };
          }

          return { items: [...state.items, item], flyToCartTrigger: item.productId };
        }),

      removeItem: (productId) =>
        set((state) => ({
          items: state.items.filter((i) => i.productId !== productId),
        })),

      updateQuantity: (productId, quantity) =>
        set((state) => {
          if (quantity <= 0) {
            return { items: state.items.filter((i) => i.productId !== productId) };
          }

          return {
            items: state.items.map((i) =>
              i.productId === productId
                ? { ...i, quantity: Math.min(quantity, i.maxQuantity) }
                : i
            ),
          };
        }),

      clearCart: () => set({ items: [] }),

      toggleOpen: () => set((state) => ({ isOpen: !state.isOpen })),

      setOpen: (open) => set({ isOpen: open }),

      triggerFlyToCart: (productId) => set({ flyToCartTrigger: productId }),

      clearFlyToCartTrigger: () => set({ flyToCartTrigger: null }),

      getSummary: () => {
        const { items } = get();
        const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
        const savings = items.reduce((sum, item) => {
          if (item.compareAtPrice) {
            return sum + (item.compareAtPrice - item.price) * item.quantity;
          }
          return sum;
        }, 0);
        const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

        return { items, subtotal, savings, itemCount };
      },

      getItemCount: () => {
        const { items } = get();
        return items.reduce((sum, item) => sum + item.quantity, 0);
      },

      isInCart: (productId) => {
        const { items } = get();
        return items.some((i) => i.productId === productId);
      },
    }),
    {
      name: STORAGE_KEY,
      partialize: (state) => ({ items: state.items }),
    }
  )
);
